import {
  Component,
  Input,
  ViewChild,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  DesignerContextType,
  DesignerCachableOutput,
} from '../../types/designer-context-type';
import {
  DesignAttributeType,
  AttributeChangeEventArgsType,
} from '../../types/designer-attribute-type';
import { NgxSmartModalService } from 'ngx-smart-modal';
import { BaseDesignerComponent } from '../designers/base/base-designer-component';
import { DynamicStyleComponent } from '../shared/dynamicStyle.component';
import { Router, ActivatedRoute } from '@angular/router';
import { CacheService } from 'app/infrastructure/services/cache.service';
import { SxInternalStateDesignerService } from '../../services/internal-state-designer-service';
import { CompleteDesignerContext } from '../main/completeContext';

@Component({
  selector: 'sx-config-modal',
  templateUrl: './config-modal.component.html',
})
export class SxConfigModalComponent {
  activeTab: 'General';

  // Title Modal
  MODAL_TITLE: string;

  // Contexto del designer.
  @Input()
  designerContext: DesignerContextType;
  // Application Context
  @Input()
  completeContext: CompleteDesignerContext;

  @Output()
  saveDesigner: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild(DynamicStyleComponent) dynamicStyle: DynamicStyleComponent;

  // Modal Tabs
  generalTab: DesignAttributeType[] = [];
  advancedTab: DesignAttributeType[] = [];
  validatorTab: DesignAttributeType[] = [];

  dynamicStylesVal: {};

  constructor(
    private ngxSmartModalService: NgxSmartModalService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private cacheService: CacheService,
    private internalState: SxInternalStateDesignerService
  ) {}

  changingTab(event) {
    this.activeTab = event.nextId;
  }

  // I call it from the html to filter the atts for type
  filterItemsOfType(type: String, tab: String) {
    const isTypeList = this.designerContext.selectedDesigner.isTypeList;
    switch (tab) {
      case 'validator':
        return this.validatorTab.filter((x) => x.type === type);
      case 'general':
        if (isTypeList) {
          return this.generalTab.filter(
            (x) =>
              x.type === type &&
              (x.name === 'maxRecords' ||
                x.name === 'minRecords' ||
                x.name === 'duplication' ||
                x.name === 'compareTo' ||
                x.name === 'compareOver' ||
                x.name === 'numericCompare' ||
                x.name === 'allowDelete' ||
                x.name === 'allowAdd')
          );
        } else {
          return this.generalTab.filter(
            (x) =>
              x.type === type &&
              x.isHidden === false &&
              x.name !== 'allowDelete' &&
              x.name !== 'allowAdd'
          );
        }
      case 'advanced':
        return this.advancedTab.filter(
          (x) => x.type === type && x.name !== 'path' && x.name !== 'layout'
        );
    }
  }

  // Muestra los atributos del designer seleccionado.
  showDesignerConfig(): void {
    // Making sure there are no att inside the tabs
    this.generalTab = [];
    this.advancedTab = [];
    this.validatorTab = [];

    if (
      this.designerContext.selectedDesignerAtts == null ||
      this.designerContext.selectedDesignerAtts.length === 0
    ) {
      return;
    }

    // De esta forma, cada vez que el valor de un atributo cambia, todos los attributos reevaluan su visibilidad
    this.designerContext.selectedDesignerAtts.forEach((att) => {
      this.designerContext.subscriptions.attributes.push(
        att.change.subscribe((value) => {
          this.attributeChanged(value);
        })
      );
    });

    // Para establecer la visibilidad inicial en base a los valores default de los campos,
    // se recorren los atributos y se emiten eventos de cambio
    this.designerContext.selectedDesignerAtts.forEach((att) => {
      att.checkInitialVisibility(this.designerContext.selectedDesignerAtts);
      // push the att into its correspondent tab
      switch (att.name) {
        case 'dependencies':
        case 'dependenciesJS':
        case 'dependencyBehaviour':
        case 'dependenciesCustomErrorJS':
        case 'customErrorMessage':
        case 'layout':
        case 'path':
        case 'hidden':
        case 'asyncDisplayDependency':
        case 'tooltip':
        case 'category':
        case 'textType':
        case 'codAtributo':
        case 'advancedColumns': {
          this.advancedTab.push(att);
          break;
        }
        case 'readOnly':
        case 'required':
        case 'causesValidation':
        case 'isPreloader':
        case 'sizeMess':
        case 'types':
        case 'maxSize':
        case 'validationGroup':
        case 'validators': {
          this.validatorTab.push(att);
          break;
        }
        default: {
          this.generalTab.push(att);
          break;
        }
      }
    });

    if (this.dynamicStyle !== undefined) this.dynamicStyle.init();

    this.getModalTitle(this.designerContext.selectedDesigner);
    this.ngxSmartModalService.setModalData({}, 'configModal');
    this.ngxSmartModalService.getModal('configModal').open();
    this.activeTab = 'General';
  }

  getModalTitle(designer: BaseDesignerComponent) {
    if (
      designer.controlContext.title !== '' &&
      designer.controlContext.title !== undefined
    ) {
      this.MODAL_TITLE = designer.controlContext.title;
    } else if (
      designer.controlContext.label !== '' &&
      designer.controlContext.label !== undefined
    ) {
      this.MODAL_TITLE = designer.controlContext.label;
    } else {
      switch (designer.type) {
        case 'SxColumns':
          return (this.MODAL_TITLE = 'COLUMNS');
        case 'SxGroup':
          return (this.MODAL_TITLE = 'GROUP');
        case 'SxGroupCollapsible':
          return (this.MODAL_TITLE = 'GROUP_COLLAPSIBLE');
        case 'SxSeparator':
          return (this.MODAL_TITLE = 'SEPARATOR');
        case 'SxModal':
          return (this.MODAL_TITLE = 'MODAL');
        case 'SxPanel':
          return (this.MODAL_TITLE = 'PANEL');
        case 'SxTitle':
          return (this.MODAL_TITLE = 'TITLE');
        case 'SxParagraph':
          return (this.MODAL_TITLE = 'PARAGRAPH');
        case 'SxImage':
          return (this.MODAL_TITLE = 'IMAGE');
        case 'SxFileSelector':
          return (this.MODAL_TITLE = 'FILE_SELECTOR');
        case 'SxFileImport':
          return (this.MODAL_TITLE = 'FILE_IMPORT');
        case 'SxActionButton':
          return (this.MODAL_TITLE = 'ACTIONBUTTON');
        case 'SxQuotationResult':
          return (this.MODAL_TITLE = 'QUOTATION_RESULT');
        case 'SxAsyncQuoteResult':
          return (this.MODAL_TITLE = 'ASYNC_QUOTATION_RESULT');
        case 'SxSyncCandidates':
          return (this.MODAL_TITLE = 'SYNC_CANDIDATES');
        case 'SxPaymentGateway':
          return (this.MODAL_TITLE = 'PAYMENT_GATEWAY');
        case 'SxViewEngine':
          return (this.MODAL_TITLE = 'VIEW_ENGINE');
        default:
          return 'CONFIGURACION_ATRIBUTO_DESIGNER';
      }
    }
  }

  attributeChanged(attribute: AttributeChangeEventArgsType) {
    this.designerContext.selectedDesignerAtts.forEach((att) => {
      att.updateVisibility(attribute.attName, attribute.newValue);
    });
  }

  okDesignerConfigurtion(): void {
    if (this.dynamicStyle) {
      this.dynamicStylesVal = this.dynamicStyle.sendDynamicStyle();
      this.designerContext.selectedDesigner.controlContext.dynamicStyle = this.dynamicStylesVal;
    }
    // Acepto la nueva configuracion
    if (this.designerContext.selectedDesignerAtts) {
      this.designerContext.selectedDesignerAtts.forEach((att) => {
        att.acceptChanges();
      });
    }

    this.designerContext.subscriptions.attributes.forEach((attSubs) => {
      attSubs.unsubscribe();
    });

    this.designerContext.subscriptions.attributes = [];

    this.ngxSmartModalService.getModal('configModal').close();
  }

  promptSubList(): void {
    this.ngxSmartModalService.getModal('goToListModal').open();
  }

  promptNewPage(propName: string): void {
    this.internalState.setCurrentPropertyModal(propName);
    this.ngxSmartModalService.getModal('goToNewPageModal').open();
  }

  promptNewFlow(propName: string): void {
    this.internalState.setCurrentPropertyModal(propName);
    this.ngxSmartModalService.getModal('createFlowModal').open();
  }

  promptNewRule(propName: string) {
    this.internalState.setCurrentPropertyModal(propName);
    this.ngxSmartModalService.getModal('goToNewRuleModal').open();
  }

  // similar to create a new page
  createNewImageAsset(event: any) {
    // firts it saves
    this.saveDesigner.emit(false);

    // only remembers the last step
    this.cacheService.removeDesignerOutputs();
    const contx: DesignerCachableOutput = {
      storedContext: this.completeContext,
      storedSelectionId: this.designerContext.selectedDesigner.id,
      propertyName: this.internalState.getCurrentPropertyModal(),
    };
    this.cacheService.setDesignerOutputs(contx);

    // depending on the context level it navigates to product assets or
    // system assets
    if (!this.designerContext.product.codNegocio) {
      // ble
    } else {
      if (this.completeContext.listPath !== undefined) {
        this.router.navigate(
          ['../../../../../../confAssets/assetFile', 'nuevo', 'nuevo'],
          { relativeTo: this.activatedRoute }
        );
      } else {
        this.router.navigate(
          ['../../../../../confAssets/assetFile', 'nuevo', 'nuevo'],
          { relativeTo: this.activatedRoute }
        );
      }
    }
  }
}



// WEBPACK FOOTER //
// D:/a/1/s/BackOffice/SOC/SxSoc.ClientWeb/src/app/private/sxfdesigner/components/modals/config-modal.component.ts